import { useEffect, useId, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Select } from '@/components/ui';
import { getErrorMessage } from '@/api';
import type {
  BulkPriceAction,
  BulkPriceColumn,
  BulkPriceFilter,
  BulkPriceInput,
  BulkPriceResult,
  ReleaseChannel,
  ReleaseChannelKey,
  SalesPlatform,
} from '@/api/admin';
import { decorationColumn, isDecorationColumn } from '@/api/admin';
import {
  useBulkChannelRelease,
  useBulkPrice,
} from '@/hooks/useAdminProductPrices';
import { parsePriceInput, priceInputToNumber } from '@/lib/price-input';
import { cn } from '@/lib/utils';
import { PLATFORM_LABEL } from './PriceSummaryStrip';
import type { PriceColumnMethod } from './ProductPriceRow';
import { releaseChannelTitle } from './ProductPriceRowCells';
import { usePriceCellsBusy } from './price-cell-activity';

/** What the bulk request addresses: the chosen ids or the list filters. */
export type BulkPriceTarget =
  | { kind: 'ids'; productIds: string[] }
  | { kind: 'filter'; filter: BulkPriceFilter };

export interface BulkPriceBarProps {
  /** Selected rows; the bar is hidden at 0. */
  count: number;
  /** "All filtered" mode: the count is the total minus the exclusions. */
  filtered: boolean;
  target: BulkPriceTarget | null;
  /** Decoration columns of the page, one bar option each. */
  columns: PriceColumnMethod[];
  /** Platform a decoration column is changed on. */
  platform: SalesPlatform;
  /** Admin release channels for the channel action (summary order). */
  releaseChannels: ReleaseChannel[];
  /** A cell is editing or saving: the preview no longer matches the table. */
  cellsBusy: boolean;
  onClearSelection: () => void;
}

/**
 * The bar as the page mounts it: reads the cell activity itself, so a
 * cell gaining or losing focus re-renders the bar and not the whole
 * table. `BulkPriceBar` keeps `cellsBusy` as a prop for the tests.
 */
export function PriceTableBulkBar(props: Omit<BulkPriceBarProps, 'cellsBusy'>) {
  const cellsBusy = usePriceCellsBusy();
  return <BulkPriceBar {...props} cellsBusy={cellsBusy} />;
}

/** Pause after the last input before the preview request leaves. */
export const BULK_PREVIEW_DEBOUNCE_MS = 300;

/** Most skipped product names the preview line spells out. */
const MAX_SKIPPED_NAMES = 20;

const TOAST_OPTS = { position: 'bottom-right' as const };

const ACTION_LABEL: Record<BulkPriceAction, string> = {
  set: 'Set to',
  add: 'Add',
  percent: 'Change by percent',
  reset: 'Reset',
};

const ACTIONS: BulkPriceAction[] = ['set', 'add', 'percent', 'reset'];

const INVALID_VALUE = 'Enter a number with up to two decimals';
const INVALID_SET_VALUE = 'A set price cannot be negative';

type PreviewState =
  | { kind: 'idle' }
  | { kind: 'calculating' }
  | { kind: 'ready'; input: BulkPriceInput; result: BulkPriceResult }
  | { kind: 'error'; message: string };

type ParsedValue =
  | { kind: 'none' }
  | { kind: 'empty' }
  | { kind: 'invalid'; message: string }
  | { kind: 'value'; value: number };

function parseValue(action: BulkPriceAction, raw: string): ParsedValue {
  if (action === 'reset') return { kind: 'none' };
  const allowNegative = action !== 'set';
  const parsed = parsePriceInput(raw, { allowNegative });
  if (parsed.kind === 'empty') return { kind: 'empty' };
  if (parsed.kind === 'invalid') {
    const negative = raw.trim().startsWith('-');
    return {
      kind: 'invalid',
      message: negative && !allowNegative ? INVALID_SET_VALUE : INVALID_VALUE,
    };
  }
  return { kind: 'value', value: priceInputToNumber(parsed.value) };
}

/** The preview line: counts, the skipped names and the cleared variants. */
export function describePreview(result: BulkPriceResult): string {
  const parts = [`${result.changed.length} will change`];
  const skipped = result.skipped.length;
  if (skipped === 0) {
    parts.push('0 skipped');
  } else {
    const names = result.skipped
      .slice(0, MAX_SKIPPED_NAMES)
      .map((skip) => skip.name);
    const more = skipped - names.length;
    parts.push(
      `${skipped} skipped: ${names.join(', ')}${more > 0 ? ` and ${more} more` : ''}`
    );
  }
  const clamped = result.changed.filter((change) => change.clamped).length;
  if (clamped > 0) parts.push(`${clamped} raised to 0.00`);
  if (result.clearedVariantPrices > 0) {
    parts.push(`${result.clearedVariantPrices} variant prices will be cleared`);
  }
  return parts.join(', ');
}

/**
 * Sticky bar at the bottom of the price table (R19): column, action and
 * value for the selected products, a preview through `dryRun` that every
 * input change invalidates, and Apply, which sends the preview's
 * `expected` so the API skips products changed in the meantime. Locked
 * while the preview is not ready, while a cell is editing or saving and
 * while the apply request runs. The bar stays mounted at count 0 so the
 * chosen column and action survive a cleared selection.
 */
export function BulkPriceBar({
  count,
  filtered,
  target,
  columns,
  platform,
  releaseChannels,
  cellsBusy,
  onClearSelection,
}: BulkPriceBarProps) {
  const valueErrorId = useId();
  const [column, setColumn] = useState<BulkPriceColumn>('purchasePrice');
  const [action, setAction] = useState<BulkPriceAction>('set');
  const [rawValue, setRawValue] = useState('');
  const [preview, setPreview] = useState<PreviewState>({ kind: 'idle' });
  const [applying, setApplying] = useState(false);
  const [releasing, setReleasing] = useState(false);
  /** Bumped to request the preview again with unchanged inputs. */
  const [previewRun, setPreviewRun] = useState(0);

  const bulk = useBulkPrice();
  // Read through refs inside effects: neither the mutation object nor the
  // target's identity should restart a preview.
  const mutateRef = useRef(bulk.mutateAsync);
  mutateRef.current = bulk.mutateAsync;
  const targetRef = useRef(target);
  targetRef.current = target;
  const applyingRef = useRef(false);
  /** Identifies the latest preview request; older answers are dropped. */
  const requestSeq = useRef(0);

  const columnOptions = useMemo<{ value: BulkPriceColumn; label: string }[]>(
    () => [
      { value: 'purchasePrice', label: 'Blank cost' },
      { value: 'recommendedSalesPrice', label: 'Rec. sales price' },
      ...columns.map(({ method }) => ({
        value: decorationColumn(method.id),
        label: method.name,
      })),
    ],
    [columns]
  );
  // A method column can disappear with a platform switch; fall back.
  const effectiveColumn = columnOptions.some(
    (option) => option.value === column
  )
    ? column
    : 'purchasePrice';
  const decoration = isDecorationColumn(effectiveColumn);
  const parsedValue = parseValue(action, rawValue);
  const valueReady =
    parsedValue.kind === 'none' || parsedValue.kind === 'value';

  /** Everything a preview depends on, as one string for the effect. */
  const previewKey = JSON.stringify({
    target,
    column: effectiveColumn,
    action,
    value: parsedValue.kind === 'value' ? parsedValue.value : null,
    platform: decoration ? platform : null,
  });

  useEffect(() => {
    const currentTarget = targetRef.current;
    if (count === 0 || currentTarget === null || !valueReady) {
      requestSeq.current += 1;
      setPreview({ kind: 'idle' });
      return;
    }
    const input: BulkPriceInput = {
      dryRun: true,
      ...(currentTarget.kind === 'ids'
        ? { productIds: currentTarget.productIds }
        : { filter: currentTarget.filter }),
      column: effectiveColumn,
      ...(decoration ? { platform } : {}),
      action,
      ...(parsedValue.kind === 'value' ? { value: parsedValue.value } : {}),
    };
    const seq = ++requestSeq.current;
    setPreview({ kind: 'calculating' });
    const timer = setTimeout(() => {
      mutateRef
        .current(input)
        .then((result) => {
          if (seq !== requestSeq.current) return;
          setPreview({ kind: 'ready', input, result });
        })
        .catch((error: unknown) => {
          if (seq !== requestSeq.current) return;
          setPreview({ kind: 'error', message: getErrorMessage(error) });
        });
    }, BULK_PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      // Whatever this run sent must not land after the next one started.
      if (seq === requestSeq.current) requestSeq.current += 1;
    };
    // The key carries target, column, action, value and platform; the
    // derived variables used inside only restate parts of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, previewRun, count, valueReady]);

  // A cell that just finished editing or saving may have moved a value the
  // preview was computed on: compute it again once the cells are quiet.
  const previousBusy = useRef(cellsBusy);
  useEffect(() => {
    if (previousBusy.current && !cellsBusy) setPreviewRun((run) => run + 1);
    previousBusy.current = cellsBusy;
  }, [cellsBusy]);

  const apply = async () => {
    if (preview.kind !== 'ready' || applyingRef.current || cellsBusy) return;
    applyingRef.current = true;
    setApplying(true);
    try {
      const result = await mutateRef.current({
        ...preview.input,
        dryRun: false,
        expected: preview.result.expected,
      });
      const stale = result.skipped.filter(
        (skip) => skip.reason === 'changed_since_preview'
      ).length;
      toast.success(
        `Changed ${result.changed.length}, skipped ${result.skipped.length}` +
          (stale > 0 ? ` (${stale} changed since the preview)` : ''),
        TOAST_OPTS
      );
      // The table has new values now; show what a second run would do.
      setPreviewRun((run) => run + 1);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
      // A failed run may still have committed (timeout, dropped connection):
      // the old preview and its `expected` values can no longer be trusted.
      setPreviewRun((run) => run + 1);
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  };

  if (count === 0) return null;

  const canApply =
    preview.kind === 'ready' && !cellsBusy && !applying && !releasing;
  const applyHint = cellsBusy
    ? 'Finish editing the cells first'
    : preview.kind !== 'ready'
      ? 'Wait for the preview'
      : undefined;
  const valueError =
    parsedValue.kind === 'invalid' ? parsedValue.message : undefined;

  return (
    <section
      aria-label="Bulk price change"
      data-testid="bulk-price-bar"
      className="sticky bottom-0 z-20 mt-2 rounded-card bg-paper-2 p-4 shadow-[0_-8px_24px_-12px_var(--ink)] ring-1 ring-line"
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="dc-eyebrow">Selection</span>
          <span className="font-mono text-sm text-ink" data-testid="bulk-count">
            {count} selected{filtered ? ' of all filtered' : ''}
          </span>
        </div>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
          Column
          <Select
            value={effectiveColumn}
            disabled={applying}
            onChange={(event) =>
              setColumn(event.target.value as BulkPriceColumn)
            }
          >
            {columnOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        {decoration && (
          <span
            className="pb-2.5 text-sm text-mute"
            data-testid="bulk-platform"
          >
            on {PLATFORM_LABEL[platform]}
          </span>
        )}

        <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
          Action
          <Select
            value={action}
            disabled={applying}
            onChange={(event) =>
              setAction(event.target.value as BulkPriceAction)
            }
          >
            {ACTIONS.map((option) => (
              <option key={option} value={option}>
                {ACTION_LABEL[option]}
              </option>
            ))}
          </Select>
        </label>

        {action !== 'reset' && (
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={`${valueErrorId}-value`}
              className="text-sm font-medium text-ink"
            >
              Value
            </label>
            <div className="relative">
              <input
                id={`${valueErrorId}-value`}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={rawValue}
                disabled={applying}
                placeholder={action === 'percent' ? '5 or -10' : '12.80'}
                aria-invalid={valueError ? true : undefined}
                aria-describedby={valueError ? valueErrorId : undefined}
                onChange={(event) => setRawValue(event.target.value)}
                className={cn(
                  'h-[42px] w-32 rounded-control bg-paper pl-3 pr-8 text-right font-mono text-sm text-ink ring-1 ring-inset ring-line placeholder:text-mute',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  'disabled:cursor-not-allowed disabled:opacity-40',
                  valueError && 'ring-danger'
                )}
              />
              <span
                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-mute"
                aria-hidden="true"
              >
                {action === 'percent' ? '%' : '€'}
              </span>
            </div>
            {valueError && (
              <p id={valueErrorId} className="text-xs text-danger">
                {valueError}
              </p>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={applying || releasing}
            onClick={onClearSelection}
          >
            Clear selection
          </Button>
          <Button
            type="button"
            disabled={!canApply}
            loading={applying}
            loadingLabel="Applying…"
            title={applyHint}
            onClick={() => void apply()}
          >
            Apply
          </Button>
        </div>
      </div>

      <p
        className={cn(
          'mt-3 text-sm',
          preview.kind === 'error' ? 'text-danger' : 'text-ink-2'
        )}
        aria-live="polite"
        data-testid="bulk-preview"
      >
        {preview.kind === 'idle' &&
          (valueError
            ? 'Fix the value to see a preview.'
            : 'Enter a value to see what would change.')}
        {preview.kind === 'calculating' && 'Calculating…'}
        {preview.kind === 'ready' && describePreview(preview.result)}
        {preview.kind === 'error' && (
          <>
            Preview failed: {preview.message}{' '}
            <button
              type="button"
              onClick={() => setPreviewRun((run) => run + 1)}
              className="font-semibold underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Retry
            </button>
          </>
        )}
      </p>

      {releaseChannels.length > 0 && (
        <ChannelReleaseActions
          count={count}
          filtered={filtered}
          target={target}
          releaseChannels={releaseChannels}
          locked={applying}
          onRunningChange={setReleasing}
          onDone={onClearSelection}
        />
      )}
    </section>
  );
}

/** "12 selected" or "all 87 in filter". */
export function describeReleaseScope(count: number, filtered: boolean): string {
  return filtered ? `all ${count} in filter` : `${count} selected`;
}

/** The confirmation of a bulk withdrawal. */
export function withdrawQuestion(count: number, channelName: string): string {
  return `Withdraw ${count} ${count === 1 ? 'product' : 'products'} from ${channelName}?`;
}

/** The toast after a bulk release: changed, unchanged, skipped inactive. */
export function describeReleaseResult(result: {
  changed: number;
  unchanged: number;
  skippedInactive: number;
}): string {
  return `${result.changed} changed, ${result.unchanged} unchanged, ${result.skippedInactive} inactive skipped`;
}

/**
 * Channel part of the bulk bar (R17): release or withdraw one admin
 * channel for the same selection as the price action. Withdrawing asks
 * for a confirmation inside the bar first. While the request runs every
 * button is locked; a success clears the selection, a failure keeps it.
 */
function ChannelReleaseActions({
  count,
  filtered,
  target,
  releaseChannels,
  locked,
  onRunningChange,
  onDone,
}: {
  count: number;
  filtered: boolean;
  target: BulkPriceTarget | null;
  releaseChannels: ReleaseChannel[];
  /** The price action runs. */
  locked: boolean;
  onRunningChange: (running: boolean) => void;
  onDone: () => void;
}) {
  const selectId = useId();
  const [channelKey, setChannelKey] = useState<ReleaseChannelKey | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [running, setRunning] = useState(false);
  const runningRef = useRef(false);
  const bulkRelease = useBulkChannelRelease();

  const channel =
    releaseChannels.find((candidate) => candidate.key === channelKey) ??
    releaseChannels[0];
  const scope = describeReleaseScope(count, filtered);
  const disabled = locked || running || target === null;

  const run = async (released: boolean) => {
    if (!channel || target === null || runningRef.current || locked) return;
    runningRef.current = true;
    setRunning(true);
    onRunningChange(true);
    try {
      const result = await bulkRelease.mutateAsync({
        channelKey: channel.key,
        released,
        ...(target.kind === 'ids'
          ? { productIds: target.productIds }
          : { filter: target.filter }),
      });
      toast.success(
        `${released ? 'Released on' : 'Withdrawn from'} ${channel.name}: ${describeReleaseResult(result)}`,
        TOAST_OPTS
      );
      setConfirmWithdraw(false);
      onDone();
    } catch (error) {
      toast.error(
        `Could not update ${channel.name}: ${getErrorMessage(error)}`,
        TOAST_OPTS
      );
    } finally {
      runningRef.current = false;
      setRunning(false);
      onRunningChange(false);
    }
  };

  if (!channel) return null;

  return (
    <div
      className="mt-3 flex flex-wrap items-end gap-4 border-t border-line pt-3"
      data-testid="bulk-channel-release"
    >
      <div className="flex flex-col gap-1.5">
        <label htmlFor={selectId} className="text-sm font-medium text-ink">
          Channel
        </label>
        <Select
          id={selectId}
          value={channel.key}
          disabled={disabled}
          onChange={(event) => {
            setChannelKey(event.target.value as ReleaseChannelKey);
            setConfirmWithdraw(false);
          }}
        >
          {releaseChannels.map((option) => (
            <option key={option.key} value={option.key}>
              {releaseChannelTitle(option)}
            </option>
          ))}
        </Select>
      </div>

      <span
        className="pb-2.5 font-mono text-sm text-ink-2"
        data-testid="bulk-channel-scope"
      >
        {scope}
      </span>

      {confirmWithdraw ? (
        <div
          role="group"
          aria-label="Confirm withdrawal"
          className="flex flex-wrap items-center gap-2"
        >
          <span className="text-sm text-ink">
            {withdrawQuestion(count, channel.name)}
          </span>
          <Button
            type="button"
            variant="danger"
            disabled={disabled}
            loading={running}
            loadingLabel="Withdrawing…"
            onClick={() => void run(false)}
          >
            Confirm
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={running}
            onClick={() => setConfirmWithdraw(false)}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            disabled={disabled}
            loading={running}
            loadingLabel="Releasing…"
            onClick={() => void run(true)}
          >
            Release
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => setConfirmWithdraw(true)}
          >
            Withdraw
          </Button>
        </div>
      )}
    </div>
  );
}
